/**
 * Incident Service - Frontend service for incident operations
 * Epic 6: Quality/Incidents/Exceptions
 */

import * as incidentRepository from "../repositories/incidentRepository";
import { logError } from "../../../shared/utils/logger.js";

/**
 * Get all incidents for a job
 * @param {string} jobId - Job identifier
 * @returns {Promise<Object>} Incidents data
 */
export const getIncidentsByJobId = async (jobId) => {
  try {
    const response = await incidentRepository.fetchIncidentsByJobId(jobId);
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch incidents");
    }
    return {
      success: true,
      data: response.data || [],
    };
  } catch (error) {
    logError("Failed to fetch incidents", {
      jobId,
      error: error.message,
    });
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
};

/**
 * Get a single incident by ID
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @returns {Promise<Object>} Incident data
 */
export const getIncidentById = async (jobId, incidentId) => {
  try {
    const response = await incidentRepository.fetchIncidentById(jobId, incidentId);
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch incident");
    }
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError("Failed to fetch incident", {
      jobId,
      incidentId,
      error: error.message,
    });
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Create a new incident
 * @param {string} jobId - Job identifier
 * @param {Object} incidentData - Incident data
 * @returns {Promise<Object>} Created incident
 */
export const createIncident = async (jobId, incidentData) => {
  try {
    const response = await incidentRepository.createIncident(jobId, incidentData);
    if (!response.success) {
      throw new Error(response.error || "Failed to create incident");
    }
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to create incident", {
      jobId,
      incidentData,
      error: error.message,
    });
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Update incident status
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @param {string} status - New status
 * @param {Object} context - Context (actorId, reason)
 * @returns {Promise<Object>} Updated incident
 */
export const updateIncidentStatus = async (jobId, incidentId, status, context = {}) => {
  try {
    const response = await incidentRepository.updateIncidentStatus(
      jobId,
      incidentId,
      status,
      context
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to update incident status");
    }
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to update incident status", {
      jobId,
      incidentId,
      status,
      error: error.message,
    });
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Add evidence to an incident
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @param {File} file - Evidence file
 * @param {string} description - Evidence description
 * @returns {Promise<Object>} Updated incident
 */
export const addIncidentEvidence = async (jobId, incidentId, file, description = "") => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (description) {
      formData.append("description", description);
    }

    const response = await incidentRepository.addIncidentEvidence(
      jobId,
      incidentId,
      formData
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to add evidence");
    }
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to add evidence", {
      jobId,
      incidentId,
      error: error.message,
    });
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Attribute responsibility for an incident
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @param {string} responsibility - Responsibility (PROVIDER, CUSTOMER, OPS, SHARED, UNDETERMINED)
 * @param {string} actorId - Actor ID
 * @param {string} reason - Reason/rationale for attribution (sent as rationale to API)
 * @returns {Promise<Object>} Updated incident
 */
export const attributeResponsibility = async (
  jobId,
  incidentId,
  responsibility,
  actorId,
  reason = ""
) => {
  try {
    const response = await incidentRepository.attributeResponsibility(jobId, incidentId, {
      responsibility,
      actorId,
      rationale: reason,
    });
    if (!response.success) {
      throw new Error(response.error || "Failed to attribute responsibility");
    }
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    logError("Failed to attribute responsibility", {
      jobId,
      incidentId,
      responsibility,
      error: error.message,
    });
    return {
      success: false,
      error: error.message,
    };
  }
};
